import React, { useMemo, useState } from 'react';

import Dropdown from '@/componentsv2/Dropdown';
import { canReviewConsultation } from '@/consultation/reviewer';
import { useAppSelector } from '@/store';

import { ConsultationCard } from './ConsultationCard';
import type { ConsultationCardItem } from './ConsultationCard';
import { DashboardCard } from './DashboardCard';
import { dashboardCurrency } from './useDashboardData';
import type { DashboardContext, useDashboardConsultations } from './useDashboardData';

interface RecentConsultationsStripProps {
  consultations: ReturnType<typeof useDashboardConsultations>['consultations'];
  loading: boolean;
  error?: boolean;
  /** Card heading; the team My-activity layout overrides to "My Consultations". */
  title?: string;
  layout?: 'horizontal' | 'vertical';
  dashCtx: DashboardContext;
  collapseButton?: React.ReactNode;
  collapsed?: boolean;
}

type Consultation = RecentConsultationsStripProps['consultations'][number];

type ConsultationSort = 'date' | 'expert' | 'duration' | 'rating';

const SORT_OPTIONS: Array<{ value: ConsultationSort; display: string }> = [
  { value: 'date', display: 'Most recent' },
  { value: 'expert', display: 'Expert name' },
  { value: 'duration', display: 'Longest' },
  { value: 'rating', display: 'Highest rated' },
];

const MAX_CONSULTATIONS = 10;

function toItem(
  c: Consultation,
  currency: 'USD' | 'OFC',
  viewerId: string | null | undefined
): ConsultationCardItem {
  const rawCents = currency === 'USD' ? c.usd_charge_amount?.cents : c.credits_used?.cents;
  return {
    id: c.id,
    htmlUrl: c.html_url,
    note: c.description ?? c.expert_request?.name ?? 'Consultation',
    date: c.starts_at,
    durationMinutes: c.billing_duration ?? null,
    costCents: rawCents ? parseInt(rawCents, 10) || 0 : 0,
    engagementType: c.engagement_type,
    projectName: c.expert_request?.project?.name ?? null,
    rating: c.expert_review?.expert_usefulness_rating ?? null,
    hasReview: !!c.expert_review,
    canReview: canReviewConsultation(viewerId, c),
    valueTags: [],
    expert: {
      name: c.expert?.name ?? 'Unknown Expert',
      firstName: c.expert?.first_name,
      lastName: c.expert?.last_name,
      pictureUrl: c.expert?.picture_url,
    },
  };
}

export function RecentConsultationsStrip({
  consultations,
  loading,
  error,
  title = 'Recent Consultations',
  layout = 'horizontal',
  dashCtx,
  collapseButton,
  collapsed,
}: RecentConsultationsStripProps): React.ReactNode {
  const [sort, setSort] = useState<ConsultationSort>('date');
  const currency = dashboardCurrency(dashCtx);
  const viewerId = useAppSelector((state) => state.viewer.id);

  const sorted = useMemo(() => {
    const visible = consultations.slice(0, MAX_CONSULTATIONS);
    return [...visible].sort((a, b) => {
      switch (sort) {
        case 'date':
          return (b.starts_at ?? '').localeCompare(a.starts_at ?? '');
        case 'expert':
          return (a.expert?.name ?? '').localeCompare(b.expert?.name ?? '');
        case 'duration':
          return (b.billing_duration ?? 0) - (a.billing_duration ?? 0);
        case 'rating':
          return (
            (b.expert_review?.expert_usefulness_rating ?? -1) -
            (a.expert_review?.expert_usefulness_rating ?? -1)
          );
      }
    });
  }, [consultations, sort]);

  return (
    <DashboardCard
      title={title}
      headerRight={
        sorted.length > 0 ? (
          <Dropdown
            id="consultations-sort"
            options={SORT_OPTIONS}
            value={sort}
            onChange={(v) => setSort(v as ConsultationSort)}
            buttonProps={{
              variant: 'tertiary',
              className: '!text-12 !font-medium !text-brand-primary',
            }}
            menuItemClassName="!text-12 !font-medium !text-brand-primary"
          />
        ) : undefined
      }
      linkText="View history"
      linkTo="/consultations"
      loading={loading}
      error={error}
      collapseButton={collapseButton}
      collapsed={collapsed}
    >
      {sorted.length === 0 ? (
        <p className="text-12 text-on-surface-muted">No completed consultations yet.</p>
      ) : layout === 'vertical' ? (
        <div className="flex flex-col gap-10" role="list">
          {sorted.map((c) => (
            <ConsultationCard
              key={c.id}
              consultation={toItem(c, currency, viewerId)}
              currency={currency}
              layout="row"
            />
          ))}
        </div>
      ) : (
        <div className="flex snap-x snap-mandatory gap-12 overflow-x-auto pb-4" role="list">
          {sorted.map((c) => (
            <ConsultationCard
              key={c.id}
              consultation={toItem(c, currency, viewerId)}
              currency={currency}
              layout="compact"
            />
          ))}
        </div>
      )}
    </DashboardCard>
  );
}
